import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  AccountBalance as ARIcon,
  Assignment as TaskIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { AccountsReceivable as ARType, FollowUpTask, PaymentPlan, DenialManagement } from '../../types/Billing';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`ar-tabpanel-${index}`}
      aria-labelledby={`ar-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AccountsReceivable: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [accountsReceivable, setAccountsReceivable] = useState<ARType[]>([]);
  const [followUpTasks, setFollowUpTasks] = useState<FollowUpTask[]>([]);
  const [denialManagement, setDenialManagement] = useState<DenialManagement[]>([]);
  const [paymentPlans, setPaymentPlans] = useState<PaymentPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [agingFilter, setAgingFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState<FollowUpTask | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Mock data - in real app, these would be API calls
      const mockAR: ARType[] = [
        {
          patientId: 'PAT-001',
          patientName: 'John Smith',
          totalBalance: 1245.67,
          insuranceBalance: 980.50,
          patientBalance: 265.17,
          agingBuckets: [
            { range: '0-30', balance: 450.00, claimCount: 3 },
            { range: '31-60', balance: 320.50, claimCount: 2 },
            { range: '61-90', balance: 275.17, claimCount: 2 },
            { range: '91-120', balance: 200.00, claimCount: 1 },
            { range: '120+', balance: 0, claimCount: 0 }
          ],
          lastPaymentDate: '2024-01-10T00:00:00Z',
          lastPaymentAmount: 150.00,
          followUpTasks: []
        },
        {
          patientId: 'PAT-002',
          patientName: 'Jane Doe',
          totalBalance: 890.25,
          insuranceBalance: 650.25,
          patientBalance: 240.00,
          agingBuckets: [
            { range: '0-30', balance: 240.00, claimCount: 2 },
            { range: '31-60', balance: 350.25, claimCount: 1 },
            { range: '61-90', balance: 300.00, claimCount: 1 },
            { range: '91-120', balance: 0, claimCount: 0 },
            { range: '120+', balance: 0, claimCount: 0 }
          ],
          followUpTasks: []
        }
      ];

      const mockTasks: FollowUpTask[] = [
        {
          id: 'task-001',
          type: 'denial_review',
          priority: 'high',
          description: 'Review denial for claim CLM-2024-001 - Medical necessity questioned',
          dueDate: '2024-01-25T00:00:00Z',
          assignedTo: 'billing-specialist-1',
          status: 'pending',
          claimId: 'claim-001',
          notes: 'Need to gather additional medical records',
          createdAt: '2024-01-20T09:00:00Z',
          updatedAt: '2024-01-20T09:00:00Z'
        },
        {
          id: 'task-002',
          type: 'patient_contact',
          priority: 'medium',
          description: 'Contact patient for payment arrangement - balance $265.17',
          dueDate: '2024-01-30T00:00:00Z',
          status: 'pending',
          notes: 'Patient has expressed financial hardship',
          createdAt: '2024-01-18T14:00:00Z',
          updatedAt: '2024-01-18T14:00:00Z'
        },
        {
          id: 'task-003',
          type: 'insurance_inquiry',
          priority: 'urgent',
          description: 'Follow up on claim status - no response for 45 days',
          dueDate: '2024-01-22T00:00:00Z',
          assignedTo: 'billing-specialist-2',
          status: 'in_progress',
          claimId: 'claim-003',
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-21T11:00:00Z'
        }
      ];

      const mockDenials: DenialManagement[] = [
        {
          id: 'denial-001',
          claimId: 'claim-001',
          denialDate: '2024-01-15T00:00:00Z',
          denialReason: 'Medical necessity not established',
          denialCode: 'CO-50',
          denialAmount: 125.00,
          appealDeadline: '2024-02-15T00:00:00Z',
          appealStatus: 'not_appealed',
          workCategory: 'medical_necessity',
          assignedTo: 'billing-specialist-1',
          notes: 'Need physician documentation'
        },
        {
          id: 'denial-002',
          claimId: 'claim-002',
          denialDate: '2024-01-12T00:00:00Z',
          denialReason: 'Duplicate claim',
          denialCode: 'CO-18',
          denialAmount: 75.00,
          appealDeadline: '2024-02-12T00:00:00Z',
          appealStatus: 'appealed',
          appealDate: '2024-01-20T00:00:00Z',
          workCategory: 'billing_error',
          assignedTo: 'billing-specialist-2'
        }
      ];

      const mockPaymentPlans: PaymentPlan[] = [
        {
          id: 'plan-001',
          patientId: 'PAT-001',
          totalAmount: 500.00,
          monthlyPayment: 50.00,
          remainingBalance: 300.00,
          startDate: '2024-01-01T00:00:00Z',
          endDate: '2024-10-01T00:00:00Z',
          status: 'active',
          payments: [
            {
              id: 'payment-001',
              dueDate: '2024-01-01T00:00:00Z',
              amount: 50.00,
              paidDate: '2024-01-05T00:00:00Z',
              paidAmount: 50.00,
              status: 'paid'
            },
            {
              id: 'payment-002',
              dueDate: '2024-02-01T00:00:00Z',
              amount: 50.00,
              paidDate: '2024-02-03T00:00:00Z',
              paidAmount: 50.00,
              status: 'paid'
            },
            {
              id: 'payment-003',
              dueDate: '2024-03-01T00:00:00Z',
              amount: 50.00,
              status: 'pending'
            }
          ]
        }
      ];

      setAccountsReceivable(mockAR);
      setFollowUpTasks(mockTasks);
      setDenialManagement(mockDenials);
      setPaymentPlans(mockPaymentPlans);
    } catch (error) {
      console.error('Error loading A/R data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleDateString();
  };

  const formatDateTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'in_progress':
        return 'info';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getTaskTypeIcon = (type: string) => {
    switch (type) {
      case 'denial_review':
        return <WarningIcon />;
      case 'patient_contact':
        return <PhoneIcon />;
      case 'insurance_inquiry':
        return <BusinessIcon />;
      case 'appeal':
        return <TaskIcon />;
      case 'documentation_request':
        return <TaskIcon />;
      default:
        return <TaskIcon />;
    }
  };

  const getPaymentPlanStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'defaulted':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  // Calculate totals
  const getTotalAR = () => {
    return accountsReceivable.reduce((sum, ar) => sum + ar.totalBalance, 0);
  };

  const getInsuranceAR = () => {
    return accountsReceivable.reduce((sum, ar) => sum + ar.insuranceBalance, 0);
  };

  const getPatientAR = () => {
    return accountsReceivable.reduce((sum, ar) => sum + ar.patientBalance, 0);
  };

  const getOverDueCount = () => {
    return followUpTasks.filter(task => {
      const dueDate = new Date(task.dueDate);
      const today = new Date();
      return dueDate < today && task.status !== 'completed';
    }).length;
  };

  const getHighPriorityCount = () => {
    return followUpTasks.filter(task => task.priority === 'urgent' || task.priority === 'high').length;
  };

  const getDenialAmount = () => {
    return denialManagement.reduce((sum, denial) => sum + denial.denialAmount, 0);
  };

  const filteredTasks = followUpTasks.filter(task => {
    const matchesSearch = task.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (task.assignedTo && task.assignedTo.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  const handleViewTask = (task: FollowUpTask) => {
    setSelectedTask(task);
    setOpenTaskDialog(true);
  };

  const handleAddTask = () => {
    setSelectedTask(null);
    setOpenTaskDialog(true);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          Accounts Receivable Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadData}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ARIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Total A/R
                </Typography>
              </Box>
              <Typography variant="h4" color="primary">
                {formatCurrency(getTotalAR())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {accountsReceivable.length} accounts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BusinessIcon color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Insurance A/R
                </Typography>
              </Box>
              <Typography variant="h4" color="info.main">
                {formatCurrency(getInsuranceAR())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Primary responsibility
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Patient A/R
                </Typography>
              </Box>
              <Typography variant="h4" color="warning.main">
                {formatCurrency(getPatientAR())}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Patient responsibility
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <WarningIcon color="error" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Overdue Tasks
                </Typography>
              </Box>
              <Typography variant="h4" color="error.main">
                {getOverDueCount()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Require immediate attention
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={1} sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          variant="fullWidth"
        >
          <Tab 
            label="A/R Summary" 
            icon={<ARIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Follow-Up Tasks" 
            icon={<TaskIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Denial Management" 
            icon={<WarningIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Payment Plans" 
            icon={<PaymentIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      <TabPanel value={currentTab} index={0}>
        {/* A/R Summary Tab */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Accounts Receivable by Patient
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Patient</TableCell>
                    <TableCell align="right">Total Balance</TableCell>
                    <TableCell align="right">Insurance</TableCell>
                    <TableCell align="right">Patient</TableCell>
                    <TableCell>Aging Distribution</TableCell>
                    <TableCell>Last Payment</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accountsReceivable.map((ar) => (
                    <TableRow key={ar.patientId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {ar.patientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ar.patientId}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(ar.totalBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(ar.insuranceBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(ar.patientBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ width: '100%' }}>
                          {ar.agingBuckets.map((bucket, index) => (
                            <Box key={bucket.range} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {bucket.range}:
                              </Typography>
                              <Typography variant="caption" fontWeight="medium">
                                {formatCurrency(bucket.balance)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {ar.lastPaymentDate ? (
                          <Box>
                            <Typography variant="body2">
                              {formatCurrency(ar.lastPaymentAmount || 0)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(ar.lastPaymentDate)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            No payments
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small">
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        {/* Follow-Up Tasks Tab */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <TextField
                label="Search tasks"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
                }}
                sx={{ minWidth: 250 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={priorityFilter}
                  label="Priority"
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <MenuItem value="all">All Priorities</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddTask}
              >
                Add Task
              </Button>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Task</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Assigned To</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTasks.map((task) => (
                    <TableRow key={task.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getTaskTypeIcon(task.type)}
                          <Box sx={{ ml: 1 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {task.description}
                            </Typography>
                            {task.claimId && (
                              <Typography variant="caption" color="text.secondary">
                                Claim: {task.claimId}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={task.type.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={task.priority}
                          color={getPriorityColor(task.priority) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(task.dueDate)}
                        </Typography>
                        {new Date(task.dueDate) < new Date() && task.status !== 'completed' && (
                          <Typography variant="caption" color="error">
                            Overdue
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {task.assignedTo || 'Unassigned'}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={task.status.replace('_', ' ')}
                          color={getStatusColor(task.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Task">
                          <IconButton 
                            size="small" 
                            onClick={() => handleViewTask(task)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        {/* Denial Management Tab */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Denial Management
              </Typography>
              <Typography variant="h6" color="error">
                Total Denied: {formatCurrency(getDenialAmount())}
              </Typography>
            </Box>

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Claim ID</TableCell>
                    <TableCell>Denial Reason</TableCell>
                    <TableCell>Denial Code</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Appeal Deadline</TableCell>
                    <TableCell>Appeal Status</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Assigned To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {denialManagement.map((denial) => (
                    <TableRow key={denial.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {denial.claimId}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Denied: {formatDate(denial.denialDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {denial.denialReason}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={denial.denialCode}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(denial.denialAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(denial.appealDeadline)}
                        </Typography>
                        {new Date(denial.appealDeadline) < new Date() && denial.appealStatus === 'not_appealed' && (
                          <Typography variant="caption" color="error" display="block">
                            Deadline passed
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={denial.appealStatus.replace('_', ' ')}
                          color={denial.appealStatus === 'approved' ? 'success' : 
                                denial.appealStatus === 'denied' ? 'error' : 
                                denial.appealStatus === 'appealed' ? 'info' : 'warning'}
                          size="small"
                        />
                        {denial.appealDate && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Appealed: {formatDate(denial.appealDate)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={denial.workCategory.replace('_', ' ')}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {denial.assignedTo || 'Unassigned'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        {/* Payment Plans Tab */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">
                Payment Plans
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
              >
                Create Payment Plan
              </Button>
            </Box>

            {paymentPlans.map((plan) => (
              <Accordion key={plan.id}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 2 }}>
                    <Typography variant="body1" fontWeight="medium">
                      Patient: {plan.patientId}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Chip 
                        label={plan.status}
                        color={getPaymentPlanStatusColor(plan.status) as any}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        Remaining: {formatCurrency(plan.remainingBalance)}
                      </Typography>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Total Amount</Typography>
                      <Typography variant="body2">{formatCurrency(plan.totalAmount)}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Monthly Payment</Typography>
                      <Typography variant="body2">{formatCurrency(plan.monthlyPayment)}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">Start Date</Typography>
                      <Typography variant="body2">{formatDate(plan.startDate)}</Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">End Date</Typography>
                      <Typography variant="body2">{formatDate(plan.endDate)}</Typography>
                    </Grid>
                  </Grid>

                  <Typography variant="subtitle2" gutterBottom>
                    Payment History
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Due Date</TableCell>
                          <TableCell align="right">Amount Due</TableCell>
                          <TableCell>Paid Date</TableCell>
                          <TableCell align="right">Amount Paid</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {plan.payments.map((payment) => (
                          <TableRow key={payment.id}>
                            <TableCell>{formatDate(payment.dueDate)}</TableCell>
                            <TableCell align="right">{formatCurrency(payment.amount)}</TableCell>
                            <TableCell>
                              {payment.paidDate ? formatDate(payment.paidDate) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {payment.paidAmount ? formatCurrency(payment.paidAmount) : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={payment.status}
                                color={payment.status === 'paid' ? 'success' : 
                                      payment.status === 'late' ? 'error' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            ))}
          </CardContent>
        </Card>
      </TabPanel>

      {/* Task Details Dialog */}
      <Dialog 
        open={openTaskDialog} 
        onClose={() => setOpenTaskDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTask ? 'Task Details' : 'Create New Task'}
        </DialogTitle>
        <DialogContent>
          {selectedTask ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedTask.description}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{selectedTask.type.replace('_', ' ')}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Priority</Typography>
                  <Typography variant="body2">{selectedTask.priority}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Due Date</Typography>
                  <Typography variant="body2">{formatDate(selectedTask.dueDate)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Typography variant="body2">{selectedTask.status.replace('_', ' ')}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Notes</Typography>
                  <Typography variant="body2">{selectedTask.notes || 'No notes'}</Typography>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              Task creation form would be implemented here with full form fields for all task properties.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTaskDialog(false)}>Close</Button>
          {selectedTask && (
            <Button variant="contained" startIcon={<EditIcon />}>
              Edit Task
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AccountsReceivable;